import React, { createContext, useContext, useEffect, useState } from 'react';
import type { FirebaseError } from 'firebase/app';
import {
    getAuth,
    signInWithPopup,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    User as FirebaseUser,
    OAuthProvider
} from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, limit, orderBy } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { db } from '../config/firebase';

// Define Role Constants (can be moved to a separate file if preferred)
export const ROLE_ADMIN = 'admin';
export const ROLE_PROJECT_RADAR = 'project_radar';
export const ROLE_COLLABORATOR_VIEWER = 'collaborator_viewer';
export const ROLE_COLLABORATOR_ADMIN = 'collaborator_admin';
export const ROLE_COLLABORATOR_EDITOR = 'collaborator_editor';
export const ROLE_SALARY_ADMIN = 'salary_admin';
export const ROLE_SALARY_VIEW = 'salary_view';
export const ROLE_CALCULATOR = 'calculator';
export const ROLE_EVALUATION_ADMIN = 'evaluation_admin';
export const ROLE_VACATION_DP = 'vacation_dp';
export const ROLE_VACATION_VIEWER = 'vacation_viewer';
export const ROLE_SI_ADMIN = 'si_admin';
/** si_rh = "Chamados DP" na tela (o time de DP, dentro do RH) — o valor ficou si_rh de propósito:
 * trocar o rótulo não mexe em regras, functions nem em quem já tem a role. */
export const ROLE_SI_RH = 'si_rh';
/** "Publicar no Portal": a pessoa oficial dos comunicados. Hoje vale pro Portal do SI (posts,
 * documentos, relatório de ciência, moderar comentários); a ideia é reusar nos outros canais
 * (DevOps, DHO, informações gerais) quando migrarem pro Digital. */
export const ROLE_PUBLICADOR = 'publicador';
export const ROLE_RECRUITMENT_ADMIN = 'recruitment_admin';

// Define our custom AppUser interface
export interface AppUser extends FirebaseUser {
    roles: string[];
    needsProfileUpdate?: boolean;
    collaboratorId?: string;
    allocationManagerUntil?: string | null;
}

interface AuthContextType {
    user: AppUser | null;
    loading: boolean;
    signInWithMicrosoft: () => Promise<void>;
    signOut: () => Promise<void>;
    error: string | null;
    userHasRole: (role: string) => boolean;
    refreshUser: () => Promise<void>;
}



export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const [user, setUser] = useState<AppUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const auth = getAuth();
    const allowedDomains = ['@rarolabs.com.br', '@raroprestador.com.br'];

    /**
     * Mantém os custom claims de papel do token alinhados com `users/{email}.roles`.
     *
     * As security rules enxergam o token, não o Firestore: regras de `list` não podem fazer `get()`,
     * então o papel do DP é reconhecido pelo claim que `functions/src/auth/syncUserClaims.ts` grava.
     * O claim novo só entra na próxima emissão do token — quem teve papel alterado, ou quem já
     * estava logado quando o backfill rodou, seguiria sem permissão até o token expirar sozinho.
     *
     * Ler o token em cache não custa rede; o refresh forçado só acontece quando os dois divergem.
     */
    const syncRoleClaims = async (firebaseUser: FirebaseUser, roles: string[]): Promise<void> => {
        try {
            const tokenResult = await firebaseUser.getIdTokenResult();
            const claimRoles = Array.isArray(tokenResult.claims.roles)
                ? (tokenResult.claims.roles as string[])
                : [];

            const sorted = (list: string[]) => [...list].sort().join('|');
            if (sorted(claimRoles) === sorted(roles)) return;

            await firebaseUser.getIdToken(true);
        } catch (claimError) {
            // Falha aqui não impede o login: só significa que as telas que dependem de claim ainda
            // vão recusar acesso, e a próxima carga tenta de novo.
            console.warn('Não foi possível atualizar os claims de papel do token.', claimError);
        }
    };

    const fetchUserRoles = async (firebaseUser: FirebaseUser): Promise<AppUser | null> => {
        try {
            let roles: string[] = [];

            try {
                // Buscar usuário diretamente por email como chave do documento
                const userRef = doc(db, 'users', firebaseUser.email!);
                const userDocSnap = await getDoc(userRef);

                if (userDocSnap.exists()) {
                    // Usuário existe, buscar roles
                    const userData = userDocSnap.data();
                    roles = userData.roles && Array.isArray(userData.roles) ? userData.roles : [];
                }
            } catch (userError: any) {
                // Se der erro de permissão, assumimos que o usuário não tem roles (usuário comum)
                // e continuamos o fluxo para buscar dados de colaborador
                if (userError?.code === 'permission-denied') {
                    console.warn('Permissão negada ao buscar roles do usuário. Assumindo usuário sem roles administrativos.');
                    roles = [];
                } else {
                    // Outros erros devem ser logados mas não devem impedir o login básico se possível
                    console.error('Erro ao buscar documento do usuário:', userError);
                    roles = [];
                }
            }

            await syncRoleClaims(firebaseUser, roles);

            // Check if user needs profile update
            let needsProfileUpdate = false;
            let collaboratorId: string | undefined;
            let allocationManagerUntil: string | null = null;

            try {
                // First, find collaborator ID by email
                const collaboratorsRef = collection(db, 'collaborators_public');
                const collaboratorQuery = query(collaboratorsRef, where('email', '==', firebaseUser.email));
                const collaboratorSnapshot = await getDocs(collaboratorQuery);

                if (!collaboratorSnapshot.empty) {
                    const collaboratorDoc = collaboratorSnapshot.docs[0];
                    collaboratorId = collaboratorDoc.id;
                    allocationManagerUntil = collaboratorDoc.get('allocationManagerUntil') ?? null;

                    // Check collaborator_self data for update status
                    const collaboratorSelfRef = doc(db, 'collaborators_self', collaboratorId);
                    const collaboratorSelfDoc = await getDoc(collaboratorSelfRef);

                    if (collaboratorSelfDoc.exists()) {
                        const collaboratorSelfData = collaboratorSelfDoc.data();

                        // Buscar janela ativa obrigatória
                        const activeWindowQuery = query(
                            collection(db, 'updateWindows'),
                            where('isActive', '==', true),
                            where('isRequired', '==', true),
                            where('startDate', '<=', new Date().toISOString()),
                            where('endDate', '>=', new Date().toISOString()),
                            orderBy('startDate', 'desc'),
                            limit(1)
                        );

                        const activeWindowSnap = await getDocs(activeWindowQuery);

                        if (!activeWindowSnap.empty) {
                            // Há janela ativa obrigatória
                            const activeWindow = activeWindowSnap.docs[0];
                            const activeWindowId = activeWindow.id;
                            const lastUpdateWindowId = collaboratorSelfData?.updatedStatus?.byUser?.lastUpdateWindowId;

                            // Precisa atualizar se não tem registro ou é de janela diferente
                            needsProfileUpdate = !lastUpdateWindowId || lastUpdateWindowId !== activeWindowId;
                        } else {
                            // Se não há janela obrigatória ativa, o usuário NÃO PRECISA atualizar,
                            // mesmo que seu status esteja desatualizado.
                            // A atualização só é forçada dentro de uma janela ativa.
                            needsProfileUpdate = false;
                        }
                    } else {
                        // If collaborator_self doesn't exist, user definitely needs profile update
                        needsProfileUpdate = true;
                    }
                }
            } catch (collaboratorError: any) {
                if (collaboratorError?.code === 'permission-denied') {
                    // Common case: User has public profile but private profile (collaborators_self) 
                    // is missing or doesn't match email exactly. 
                    // We already have the collaboratorId from public profile, so we can proceed.
                    // Access to private data (salary, etc) will just be unavailable.
                    console.debug('Acesso a dados privados do colaborador (self) não disponível.');
                } else {
                    console.error('Erro ao buscar informações de colaborador:', collaboratorError);
                }
                // Don't fail the entire authentication if collaborator info fails
            }

            return {
                ...firebaseUser,
                roles,
                needsProfileUpdate,
                collaboratorId,
                allocationManagerUntil
            };

        } catch (e) {
            console.error("Erro ao buscar roles do usuário:", e);
            setError('Erro ao carregar informações do usuário.');
            await firebaseSignOut(auth); // Sign out on error fetching roles
            return null;
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setLoading(true); // Set loading true while fetching roles
                const appUserWithRoles = await fetchUserRoles(firebaseUser);
                if (appUserWithRoles) {
                    setUser(appUserWithRoles);
                    setError(null);
                } else {
                    setUser(null); // Ensure user is null if roles fetch fails or unauthorized
                }
            } else {
                setUser(null);
                setError(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, [auth]);

    const handlePostSignIn = async (firebaseUser: FirebaseUser): Promise<boolean> => {
        const userEmail = firebaseUser.email || '';
        const hasValidDomain = allowedDomains.some(domain => userEmail.toLowerCase().endsWith(domain));

        if (!hasValidDomain) {
            await firebaseSignOut(auth);
            const message = 'Acesso permitido apenas para contas @rarolabs.com.br ou @raroprestador.com.br';
            setError(message);
            return false;
        }

        const appUserWithRoles = await fetchUserRoles(firebaseUser);

        if (!appUserWithRoles) {
            setError('Usuário não autorizado ou erro ao buscar roles.');
            await firebaseSignOut(auth);
            return false;
        }

        setUser(appUserWithRoles);
        setError(null);
        return true;
    };

    const signInWithMicrosoft = async () => {
        try {
            setError(null);
            setLoading(true);

            const provider = new OAuthProvider('microsoft.com');
            const customParameters: Record<string, string> = { prompt: 'select_account' };
            const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID as string | undefined;

            console.log('[Auth] Configuração Microsoft:', {
                tenantIdDefined: !!tenantId,
                tenantIdLength: tenantId?.length
            });

            if (tenantId) {
                customParameters.tenant = tenantId;
            }

            provider.setCustomParameters(customParameters);

            console.log(`[Auth] Iniciando login com Microsoft...`);
            const result = await signInWithPopup(auth, provider);
            console.log(`[Auth] Login com Microsoft bem-sucedido. Usuário:`, result.user.email);

            await handlePostSignIn(result.user);
        } catch (e: any) {
            console.error(`[Auth] Erro detalhado ao fazer login com Microsoft:`, {
                code: e?.code,
                message: e?.message,
                customData: e?.customData,
                fullErrorString: JSON.stringify(e, Object.getOwnPropertyNames(e))
            });

            const errorMessage = e?.code === 'auth/admin-restricted-operation'
                ? 'Operação restrita pelo administrador. Verifique se o provedor Microsoft está habilitado e configurado corretamente no Firebase Console.'
                : (e instanceof Error ? e.message : `Erro ao fazer login com Microsoft`);

            console.error(`Erro ao fazer login com Microsoft:`, e);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const signOut = async () => {
        try {
            await firebaseSignOut(auth);
            queryClient.clear();
            setUser(null); // Clear user state on sign out
            setError(null);
        } catch (e) {
            console.error('Erro ao fazer logout:', e);
            setError('Erro ao fazer logout');
            // No need to throw error further up if handled here
        }
    };

    const userHasRole = (role: string): boolean => {
        return user?.roles?.includes(role) || false;
    };

    const refreshUser = async (): Promise<void> => {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
            const updatedUser = await fetchUserRoles(firebaseUser);
            setUser(updatedUser);
        }
    };

    const value = {
        user,
        loading,
        signInWithMicrosoft,
        signOut,
        error,
        userHasRole,
        refreshUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 
